import type { Language } from "@newgenerated/shared/schema";
import { LanguageHelper } from "@utils/type/type";
import { dictToList } from "@utils/dictUtils";
import type { AspContext } from "@utils/widgetContextUtils";

export type UrlContext = { currentLanguage: Language; asp: AspContext };

export function enrichRelativeUrl(relativeUrl: string, ctx: UrlContext): string {
  if (!relativeUrl.startsWith("/") || URL.canParse(relativeUrl)) {
    // not a relative URL
    return relativeUrl;
  }

  // Cast to an optional shape so the feature-detect below is meaningful: the DOM lib declares
  // `location` as always-present, but this code also runs server-side (SSR/tests), where it isn't.
  const g = globalThis as { location?: { origin?: string } };

  const url = new URL(relativeUrl, g.location?.origin ?? "https://te.st");

  if (ctx.asp !== null) {
    url.searchParams.set("u", encodeURIComponent(ctx.asp.username));
  }

  const pathElements = url.pathname.split("/").filter((e) => e.trim().length > 0);

  if (LanguageHelper.isLanguage(pathElements[0] ?? "")) {
    return `/${ctx.currentLanguage}/${pathElements.slice(1).join("/")}${url.search}`;
  }
  return `/${ctx.currentLanguage}${url.pathname}${url.search}`;
}

/**
 * Serializes an object to a query string.
 * Arrays are serialized with repeated keys (arrayFormat: "repeat").
 *
 * @param params - The object to serialize
 * @param options - Serialization options
 * @param options.encode - Whether to URL encode values (default: true)
 * @returns The query string (without leading '?')
 *
 * @example
 * stringifyParams({ query: "test", lang: ["en", "de"] })
 * // Returns: "query=test&lang=en&lang=de"
 *
 * stringifyParams({ query: "hello world" }, { encode: false })
 * // Returns: "query=hello world"
 */
export function stringifyParams(params: Record<string, unknown>, options: { encode?: boolean } = {}): string {
  const encode = options.encode !== false;
  const parts: string[] = [];

  for (const [key, value] of dictToList(params)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          const encodedKey = encode ? encodeURIComponent(key) : key;
          const encodedValue = encode ? encodeURIComponent(String(item)) : String(item);
          parts.push(`${encodedKey}=${encodedValue}`);
        }
      }
    } else {
      const encodedKey = encode ? encodeURIComponent(key) : key;
      const encodedValue = encode ? encodeURIComponent(String(value)) : String(value);
      parts.push(`${encodedKey}=${encodedValue}`);
    }
  }

  return parts.join("&");
}

export class RelativeURL {
  private readonly _host = "https://internal";
  private readonly _url: URL;

  constructor(input: string | string[] | URL) {
    if (input instanceof URL) {
      this._url = new URL(input.href, this._host);
    } else if (input instanceof Array) {
      const path = this.listToPath(input);
      this._url = new URL(path, this._host);
    } else {
      const temp = new URL(input, this._host);
      this._url = new URL(temp.href, this._host);
    }
  }

  to(path: string | string[]): RelativeURL {
    let traversal: string;
    if (path instanceof Array) {
      traversal = this.listToPath(path);
    } else {
      traversal = path;
    }
    const newUrl = new URL(this.joinPaths(this._url.pathname, traversal), this._url);
    newUrl.search = this._url.search;
    return new RelativeURL(newUrl);
  }

  /* Expose some properties from the URL interface. */

  get search(): string {
    return this._url.search;
  }

  set search(search: string) {
    this._url.search = search;
  }

  get searchParams(): URLSearchParams {
    return this._url.searchParams;
  }

  get pathname(): string {
    return this._url.pathname;
  }

  set pathname(pathname: string) {
    this._url.pathname = pathname;
  }

  toUrl(baseUrl: string): URL {
    return new URL(this.toString(), baseUrl);
  }

  toString(): string {
    return `${this._url.pathname}${this._url.search}`;
  }

  private listToPath(path: string[]): string {
    return path.join("/");
  }

  private joinPaths(pathA: string, pathB: string): string {
    if (pathB.startsWith("/")) {
      return pathB;
    }
    const pathElementsA = pathA.split("/").filter((e) => e.trim().length > 0);
    const pathElementsB = pathB.split("/").filter((e) => e.trim().length > 0);

    return [...pathElementsA, ...pathElementsB].join("/");
  }
}
